import { Invoice } from "../../../../pojo/Pricing";
import { IPaymentDetails } from "../../s25-payments/payment.service";
import { InvoiceTotals } from "../invoice/totals/invoice-totals";
import { S25PricingUtil } from "../../s25.pricing.util";
import { S25Util } from "../../../../util/s25-util";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";

export namespace AccountSummaryMetrics {
    const cents = S25PricingUtil.convertToCents;
    const dollars = S25PricingUtil.convertToDollars;

    export type SummaryRangeOption = "month" | "quarter" | "ytd";
    export type DateRange = { start: Date; end: Date };

    const RANGE_LABELS: Record<SummaryRangeOption, string> = {
        month: "Month to date",
        quarter: "Quarter to date",
        ytd: "Year to date",
    };

    export function summaryRangeLabel(option: SummaryRangeOption): string {
        return RANGE_LABELS[option];
    }

    /** The calendar month, quarter or year in progress, up to the end of today. */
    export function summaryRange(option: SummaryRangeOption, now: Date): DateRange {
        const end = S25Util.date.toEndOfDay(now);

        if (option === "month") return { start: S25Util.date.firstDayOfMonth(now), end };
        if (option === "quarter") {
            const firstMonthOfQuarter = Math.floor(now.getMonth() / 3) * 3;
            return { start: new Date(now.getFullYear(), firstMonthOfQuarter, 1), end };
        }
        return { start: new Date(now.getFullYear(), 0, 1), end };
    }

    export function inRange(date: Date | undefined, range: DateRange): boolean {
        return !!date && date >= range.start && date <= range.end;
    }

    /**
     * Every payment on the account, one entry per record. An invoice and the default set can report the
     * same payment, so records are taken by id. Voids never count: they undid themselves.
     */
    export function accountPayments(invoices: Invoice[]): IPaymentDetails[] {
        const byId = new Map<number, IPaymentDetails>();

        for (const invoice of invoices) {
            for (const payment of invoice.payments ?? []) {
                if (payment.isVoid) continue;
                byId.set(payment.paymentDetailId, payment);
            }
        }
        return [...byId.values()];
    }

    export function isPaid(payment: IPaymentDetails): boolean {
        return payment.paymentStatus === "paid";
    }

    /** When money landed. A settled payment missing its success date falls back to when it was made. */
    export function paidOn(payment: IPaymentDetails): Date | undefined {
        const date = payment.paymentSuccess ?? payment.paymentCreated;
        return date ? S25Util.date.parse(date) : undefined;
    }

    function paymentCents(payment: IPaymentDetails): number {
        return cents((payment.amountTotalCents ?? 0) / 100);
    }

    /** Dollars collected, summed in cents so a long payment list cannot drift. */
    export function paidCents(payments: IPaymentDetails[]): number {
        return payments
            .filter((payment) => !payment.isVoid && isPaid(payment))
            .reduce((total, payment) => total + paymentCents(payment), 0);
    }

    export function paidTotal(payments: IPaymentDetails[]): number {
        return dollars(paidCents(payments));
    }

    /** Money asked for, never paid, and past its due date. */
    export function overdueCents(payments: IPaymentDetails[], now: Date): number {
        return payments
            .filter(
                (payment) =>
                    !payment.isVoid &&
                    !isPaid(payment) &&
                    !!payment.dueDate &&
                    S25Util.date.parse(payment.dueDate) < now,
            )
            .reduce((total, payment) => total + paymentCents(payment), 0);
    }

    function billedCents(invoice: Invoice): number {
        return cents(invoice.billing?.totals?.grandTotal ?? 0);
    }

    /** What an invoice still has to collect. */
    export function invoiceBalanceCents(invoice: Invoice): number {
        return billedCents(invoice) - paidCents(invoice.payments ?? []);
    }

    export type AccountSummaryTiles = {
        totalBilled: number;
        paid: number;
        outstanding: number;
        overdue: number;
        openInvoices: number;
    };

    /**
     * The headline figures for the whole account: every invoice the user works with, the default set
     * included. The reference set is not one of them - it records the others, so counting it would
     * double every total.
     */
    export function accountSummaryTiles(invoices: Invoice[], now: Date): AccountSummaryTiles {
        const payments = accountPayments(invoices),
            billed = invoices.reduce((total, invoice) => total + billedCents(invoice), 0),
            paid = paidCents(payments);

        return {
            totalBilled: dollars(billed),
            paid: dollars(paid),
            outstanding: dollars(billed - paid),
            overdue: dollars(overdueCents(payments, now)),
            openInvoices: invoices.filter((invoice) => invoiceBalanceCents(invoice) > 0).length,
        };
    }

    export type CollectionSplit = { total: number; paid: number; current: number; overdue: number };

    /**
     * Splits what the account is owed into paid, current and overdue. Overdue can only be what a
     * payment request went unpaid past its due date, and never more than is left to collect.
     */
    export function collectionProgressSplit(invoices: Invoice[], now: Date): CollectionSplit {
        const payments = accountPayments(invoices),
            total = invoices.reduce((sum, invoice) => sum + billedCents(invoice), 0),
            paid = paidCents(payments),
            overdue = Math.min(overdueCents(payments, now), Math.max(0, total - paid));

        return {
            total: dollars(total),
            paid: dollars(paid),
            overdue: dollars(overdue),
            current: dollars(Math.max(0, total - paid - overdue)),
        };
    }

    /** A percentage clamped to 0-100, safe for use as a CSS width/height. */
    export function percentOf(part: number, whole: number): number {
        if (!whole) return 0;
        return Math.max(0, Math.min(100, (part / whole) * 100));
    }

    export type OrgBar = { orgId: number; orgName: string; billed: number; paid: number };

    /** Every event an invoice prices, so an org's share here matches the grand total the tiles count. */
    const WHOLE_INVOICE: InvoiceTotals.InvoiceScope = { combineRelated: true, eventId: undefined };

    /**
     * What each org has been billed against what it has paid in the range. Billing carries no date a
     * payment can be read against - a charge belongs to the invoice, not to a day - so the billed figure
     * is everything the org owes, and only the payments are scoped. A payment is dated by when it
     * settled.
     */
    export function orgBilledVsPaid(
        invoices: Invoice[],
        orgNameById: ReadonlyMap<number, string | undefined>,
        range: DateRange,
    ): OrgBar[] {
        const billedByOrg = new Map<number, number>(),
            paidByOrg = new Map<number, number>();

        for (const invoice of invoices) {
            const invoiceSubtotals = InvoiceTotals.invoiceSubtotalByOrg(invoice);

            for (const [orgId, subtotals] of InvoiceTotals.subtotalsByOrg(invoice, WHOLE_INVOICE)) {
                const charge = InvoiceTotals.orgGrandTotal(
                    InvoiceTotals.sumSubtotals(subtotals),
                    invoiceSubtotals.get(orgId),
                    true,
                );
                billedByOrg.set(orgId, (billedByOrg.get(orgId) ?? 0) + cents(charge));
            }
        }

        for (const payment of accountPayments(invoices)) {
            const orgId = payment.organizationId;
            if (!orgId || !isPaid(payment) || !inRange(paidOn(payment), range)) continue;
            paidByOrg.set(orgId, (paidByOrg.get(orgId) ?? 0) + paymentCents(payment));
        }

        return [...new Set([...billedByOrg.keys(), ...paidByOrg.keys()])]
            .map((orgId): OrgBar => {
                const orgName = orgNameById.get(orgId) ?? String(orgId);
                return {
                    orgId,
                    orgName,
                    billed: dollars(billedByOrg.get(orgId) ?? 0),
                    paid: dollars(paidByOrg.get(orgId) ?? 0),
                };
            })
            .sort((first, second) => second.billed - first.billed || first.orgName.localeCompare(second.orgName));
    }

    export type PeriodBucket = "week" | "month";
    export type PeriodBar = { key: string; label: string; paid: number; balance: number };

    /**
     * How the account is being paid down: what each period collected, and what was still outstanding
     * when it closed. The rightmost period is the one holding today, and periods run back from there to
     * the start of the range. A period's balance counts every payment settled by its end, including
     * payments made before the range began.
     */
    export function bucketedPaidVsBalance(args: {
        payments: IPaymentDetails[];
        totalBilled: number;
        range: DateRange;
        bucket: PeriodBucket;
        weekStart?: number;
    }): PeriodBar[] {
        const { payments, range, bucket } = args,
            paidPayments = payments.filter((payment) => !payment.isVoid && isPaid(payment)),
            totalCents = cents(args.totalBilled),
            starts = bucketStarts(range, bucket, args.weekStart);

        const settledBy = (limit: Date) =>
            paidPayments
                .filter((payment) => {
                    const paidDate = paidOn(payment);
                    return !!paidDate && paidDate <= limit;
                })
                .reduce((total, payment) => total + paymentCents(payment), 0);

        return starts.map((start, index): PeriodBar => {
            const nextStart = starts[index + 1],
                end = nextStart ? S25Util.date.toEndOfDay(S25Util.date.addDays(nextStart, -1)) : range.end;

            const throughEnd = settledBy(end),
                beforeStart = settledBy(S25Util.date.toEndOfDay(S25Util.date.addDays(start, -1)));

            return {
                key: start.toISOString(),
                label: S25Datefilter.transform(start, bucket === "week" ? "MMM d" : "MMM yyyy") ?? "",
                paid: dollars(throughEnd - beforeStart),
                balance: dollars(Math.max(0, totalCents - throughEnd)),
            };
        });
    }

    /** Period starts across the range, oldest first, ending with the period holding the range's end. */
    function bucketStarts(range: DateRange, bucket: PeriodBucket, weekStart?: number): Date[] {
        const first =
            bucket === "week"
                ? S25Util.date.firstDayOfWeek(range.start, weekStart)
                : S25Util.date.firstDayOfMonth(range.start);
        const last =
            bucket === "week"
                ? S25Util.date.firstDayOfWeek(range.end, weekStart)
                : S25Util.date.firstDayOfMonth(range.end);

        const starts: Date[] = [];
        for (let start = first; start <= last; ) {
            starts.push(start);
            start = bucket === "week" ? S25Util.date.addDays(start, 7) : S25Util.date.addMonths(start, 1);
        }
        return starts;
    }
}
